// Fully Encoded Registry Entries with Complete M3 Meta-Stack Grammar
// These entries have been encoded using the four-layer Meta-Stack: Ontology, Modality, Dynamics, Evaluation

export interface FullyEncodedEntry {
  encodedId: string;
  ontology?: {
    category?: string[];
    structures?: string[];
    relationships?: Array<{
      relationshipType: string;
      description: string;
    }>;
    systems?: string[];
    entities?: string[];
    definitions?: Record<string, string>;
  };
  modality?: {
    attributes?: Array<{
      attributeType: string;
      measurementScale: string;
    }>;
    states?: Array<{
      stateType: string;
      stateValues: string;
    }>;
    contexts?: string[];
    constraints?: Array<{
      constraintType: string;
      description: string;
    }>;
    identity?: {
      stableId: string;
      versioning: string;
      lineage: string[];
      invariants: string;
    };
  };
  dynamics?: {
    rules?: Array<{
      ruleType: string;
      ruleDescription: string;
      sourceNotes?: string;
    }>;
    processes?: Array<{
      processType: string;
      processDescription: string;
    }>;
    timing?: Record<string, string>;
    causality?: Array<{
      causalType: string;
      causalDescription: string;
    }>;
    uncertainty?: {
      confidenceLevels?: string;
      sensitivity?: string;
      volatilityIndicators?: string;
    };
  };
  evaluation?: {
    intent?: {
      actorScope?: string[];
      stakeholders?: string[];
      timeHorizon?: string[];
      motivationalFrame?: string[];
      ethicalAnchors?: string[];
      priorityAims?: string[];
    };
    utility?: {
      utilityDimensions?: string[];
      tradeoffPolicies?: string[];
    };
    observation?: {
      metrics?: Array<{
        metricType: string;
        metricDescription: string;
      }>;
      signals?: string[];
      indicatorClasses?: string[];
      resolutionCadence?: string;
    };
    adaptation?: {
      feedbackLoops?: string[];
      scopeOfChange?: string[];
      learningDepth?: string[];
      memoryTraceability?: Record<string, boolean>;
    };
  };
}

export const fullyEncodedEntries: FullyEncodedEntry[] = [
  // DEGAP - Five Stages of DEGAP
  {
    encodedId: 'pf.degap.stages',
    ontology: {
      category: ['framework', 'process'],
      structures: ['sequence', 'phased'],
      relationships: [
        {
          relationshipType: 'part-of',
          description: 'Core component of the book\'s prioritization method'
        }
      ],
      systems: ['degap', 'organizational-planning', 'personal-prioritizing'],
      entities: [
        'Decide',
        'Engage',
        'Gather',
        'Arrange',
        'Prioritize'
      ],
      definitions: {
        items: 'Potential priorities until prioritized'
      }
    },
    modality: {
      attributes: [
        {
          attributeType: 'clarity',
          measurementScale: '1–10 clarity of next actions and rationale'
        },
        {
          attributeType: 'alignment',
          measurementScale: 'Aligned/Partially/Not aligned to North Star'
        },
        {
          attributeType: 'timeline-control',
          measurementScale: 'High/Medium/Low'
        }
      ],
      states: [
        {
          stateType: 'multi-state',
          stateValues: 'Not Started, In Progress, Completed, Blocked'
        }
      ],
      contexts: ['scope-individual', 'scope-team', 'scope-organization'],
      constraints: [
        {
          constraintType: 'time-availability',
          description: 'Whether there is time to run the process deliberately'
        },
        {
          constraintType: 'stakeholder-diversity',
          description: 'More stakeholders require more methodical approach'
        }
      ],
      identity: {
        stableId: 'degap-stages-v1',
        versioning: 'v1.0',
        lineage: ['derives-from: Managing Priorities (Max)'],
        invariants: 'Five-phase structure with optional lightweight application'
      }
    },
    dynamics: {
      rules: [
        {
          ruleType: 'recommended-sequence',
          ruleDescription: 'Proceed D→E→G→A→P; phases may be tailored or, in a pinch, a single framework may suffice',
          sourceNotes: 'Formal sequence is recommended; selective use is acceptable when appropriate'
        }
      ],
      processes: [
        {
          processType: 'state-transitions',
          processDescription: 'Sequential progression with checkpoints; Gather is typically most time-intensive; duration scales with scope and complexity'
        },
        {
          processType: 'organizational-mapping',
          processDescription: 'D formalized by execs; EGA via Catchball; P produces plan of record'
        }
      ],
      timing: {
        'solo-episodic': '≈1 hour',
        'single-team': 'couple of hours to a day',
        'multi-team/org': 'weeks to a couple of months',
        note: 'Longer when high-value info is hard to obtain'
      },
      causality: [
        {
          causalType: 'direct',
          causalDescription: 'Decide → commitment to run the process'
        },
        {
          causalType: 'quality-propagation',
          causalDescription: 'Higher-quality Gather improves Arrange/P outcomes'
        }
      ],
      uncertainty: {
        confidenceLevels: 'High confidence in usefulness across sectors; timing varies by context',
        sensitivity: 'Sensitive to stakeholder buy-in and leadership support',
        volatilityIndicators: 'Stable core; adaptable application'
      }
    },
    evaluation: {
      intent: {
        actorScope: ['individual', 'team', 'organization'],
        stakeholders: ['employees', 'managers', 'executive leadership'],
        timeHorizon: ['near', 'mid', 'long'],
        motivationalFrame: ['intrinsic', 'instrumental'],
        ethicalAnchors: ['clarity', 'transparency', 'fairness'],
        priorityAims: ['close-as-is-to-future-state-gap', 'own-the-timeline']
      },
      utility: {
        utilityDimensions: ['clarity', 'alignment', 'predictability', 'efficiency'],
        tradeoffPolicies: ['balance speed vs. rigor based on urgency and complexity']
      },
      observation: {
        metrics: [
          {
            metricType: 'timeline-control-index',
            metricDescription: 'Ability to say yes/no/not yet with confidence'
          },
          {
            metricType: 'decision-throughput',
            metricDescription: 'Decisions finalized per week'
          },
          {
            metricType: 'alignment-rate',
            metricDescription: '% priorities aligned to North Star/plan of record'
          }
        ],
        signals: ['decision logs', 'Catchball feedback', 'stakeholder interviews'],
        indicatorClasses: ['leading', 'direct'],
        resolutionCadence: 'Weekly reviews; tighter in high-urgency contexts'
      },
      adaptation: {
        feedbackLoops: ['scheduled', 'continuous'],
        scopeOfChange: ['weights', 'processes', 'stakeholder inputs'],
        learningDepth: ['single-loop', 'double-loop'],
        memoryTraceability: {
          decisionLogs: true,
          rationales: true,
          beforeAfterSnapshots: true
        }
      }
    }
  },

  // Prioritization Objects & Relationships
  {
    encodedId: 'pf.objects.relationships',
    ontology: {
      category: ['framework', 'system-model', 'entity-relationship'],
      structures: ['hierarchical', 'networked', 'lifecycle'],
      relationships: [
        {
          relationshipType: 'lifecycle',
          description: 'Items move through workflow stages (Intake → Item Work → …); priority is contextual (list membership + rank)'
        },
        {
          relationshipType: 'containment',
          description: 'Cycles contain Item Pools contain ranked Items'
        },
        {
          relationshipType: 'governance',
          description: 'Item Managers, List Owners, Criteria Authorities manage objects'
        },
        {
          relationshipType: 'shaping',
          description: 'Attributes and Values define and describe Items'
        }
      ],
      systems: ['prioritization-system', 'focus-management', 'candidate-lifecycle'],
      entities: [
        'Item',
        'Published Priority List',
        'Attribute',
        'Value',
        'Criteria',
        'Item Pool',
        'Cycle',
        'Ball Holder',
        'Decision Authority',
        'Facilitator'
      ],
      definitions: {
        item: 'The primary first-class object in the system; may be in intake or past item work',
        publishedPriorityList: 'The authoritative prioritization outcome after Ratified → Published',
        attribute: 'Fields that define Items (e.g., Impact, Effort, Risk)',
        value: 'Entries for attributes on a specific Item',
        criteria: 'Selected Attributes used to evaluate and compare Items',
        itemPool: 'The comparison set selected during Arrange; locked before S6',
        cycle: 'The highest-level container of intent for prioritization',
        ballHolder: 'The role currently responsible for advancing the Ball at a given Tier'
      }
    },
    modality: {
      attributes: [
        {
          attributeType: 'candidate-state',
          measurementScale: 'Item (structural); queue/stage: intake, item work, ranking'
        },
        {
          attributeType: 'lifecycle-stage', 
          measurementScale: 'Intake, Item Work, Slotting/Ranking'
        },
        {
          attributeType: 'attribute-type',
          measurementScale: 'Impact, Effort, Risk, Value, Urgency'
        },
        {
          attributeType: 'governance-role',
          measurementScale: 'Item Manager, List Owner, Criteria Authority, Cross-Map Lead'
        },
        {
          attributeType: 'focus-horizon',
          measurementScale: 'Q1, Q2, Q3, Q4, Annual Strategy, Sprint, Release'
        },
        {
          attributeType: 'comparison-set',
          measurementScale: 'Single Item Pool, Cross-Pool, Cross-Track'
        }
      ],
      states: [
        {
          stateType: 'item-structural-state',
          stateValues: 'Item (structural); workflow stage: intake, item work, ranking'
        },
        {
          stateType: 'item-pool-state',
          stateValues: 'Active, Locked, Archived'
        }
      ],
      contexts: ['individual-prioritization', 'team-prioritization', 'organizational-prioritization'],
      constraints: [
        {
          constraintType: 'state-transition',
          description: 'Items must have attributes with values; ranking occurs within an Item Pool'
        },
        {
          constraintType: 'criteria-consistency',
          description: 'All Items in an Item Pool must share the same Criteria Set'
        },
        {
          constraintType: 'governance-authority',
          description: 'Decision Authority governs Criteria selection at Cycle level'
        }
      ],
      identity: {
        stableId: 'prioritization-objects-v1',
        versioning: 'v1.0',
        lineage: ['derives-from: Enhanced Interrogative Taxonomy', 'integrates-with: DEGAP framework'],
        invariants: 'Item as root entity from creation; priority is contextual (list + rank); Item Pool / Published Priority List as canonical ranked output'
      }
    },
    dynamics: {
      rules: [
        {
          ruleType: 'state-transition',
          ruleDescription: 'Items progress through workflow stages (Intake → Item Work → Slotting/Ranking); priority is contextual (list membership + rank)',
          sourceNotes: 'No pre-Item stage; structural state is item only'
        },
        {
          ruleType: 'criteria-governance',
          ruleDescription: 'Criteria are defined at Cycle level via Criteria Set and must be shared by all Items in Item Pool',
          sourceNotes: 'Governed by Decision Authority'
        },
        {
          ruleType: 'item-pool-ownership',
          ruleDescription: 'Each Item Pool belongs to a Cycle and has a Ball Holder responsible for it',
          sourceNotes: 'Enables accountability and governance'
        }
      ],
      processes: [
        {
          processType: 'candidate-intake',
          processDescription: 'Items enter via intake; workflow stage is intake until item work is done'
        },
        {
          processType: 'candidate-shaping',
          processDescription: 'Items in intake get attributes and values through item work'
        },
        {
          processType: 'priority-ranking',
          processDescription: 'Items become ranked through Criteria-based evaluation within an Item Pool'
        },
        {
          processType: 'focus-drift-detection',
          processDescription: 'Focus Maps enable detection of alignment issues and drift across organization'
        }
      ],
      timing: {
        'intake-arrival': 'Immediate',
        'shaping-complete': 'During item work',
        'item-to-priority': 'During Slotting/Ranking phase',
        'focus-horizon-review': 'Based on Focus Horizon timeframe',
        note: 'Timing varies by Focus Horizon (Q1, Q2, Annual Strategy, etc.)'
      },
      causality: [
        {
          causalType: 'direct',
          causalDescription: 'Intake queue receives new items'
        },
        {
          causalType: 'shaping',
          causalDescription: 'Item work completes (attributes with values)'
        },
        {
          causalType: 'ranking',
          causalDescription: 'S6 Item Prioritization → ranked output (requires Item Pool membership)'
        },
        {
          causalType: 'governance',
          causalDescription: 'Criteria Authority → Criteria selection → ranking capability'
        }
      ],
      uncertainty: {
        confidenceLevels: 'High confidence in entity relationships; moderate confidence in governance patterns',
        sensitivity: 'Sensitive to organizational structure and decision-making culture',
        volatilityIndicators: 'Stable core entities; variable governance implementation'
      }
    },
    evaluation: {
      intent: {
        actorScope: ['individual', 'team', 'organization'],
        stakeholders: ['stewards', 'list-owners', 'criteria-authorities', 'cross-map-leads'],
        timeHorizon: ['short-term', 'medium-term', 'long-term'],
        motivationalFrame: ['clarity', 'accountability', 'alignment'],
        ethicalAnchors: ['transparency', 'fairness', 'consistency'],
        priorityAims: ['clear-responsibility', 'aligned-focus', 'effective-governance']
      },
      utility: {
        utilityDimensions: ['clarity', 'accountability', 'alignment', 'governance'],
        tradeoffPolicies: ['balance flexibility vs. structure based on organizational maturity']
      },
      observation: {
        metrics: [
          {
            metricType: 'candidate-throughput',
            metricDescription: 'Candidates processed through lifecycle per time period'
          },
          {
            metricType: 'item-pool-quality',
            metricDescription: 'Item Pool alignment and stakeholder satisfaction'
          },
          {
            metricType: 'governance-effectiveness',
            metricDescription: 'Criteria Authority and List Owner effectiveness'
          },
          {
            metricType: 'focus-drift-rate',
            metricDescription: 'Frequency of Focus Drift detection and correction'
          }
        ],
        signals: ['candidate-state-transitions', 'focus-list-reviews', 'stakeholder-feedback'],
        indicatorClasses: ['leading', 'lagging'],
        resolutionCadence: 'Based on Focus Horizon; typically quarterly or annual'
      },
      adaptation: {
        feedbackLoops: ['focus-horizon-reviews', 'criteria-effectiveness-assessments'],
        scopeOfChange: ['criteria-selection', 'governance-roles', 'focus-horizon-settings'],
        learningDepth: ['single-loop', 'double-loop'],
        memoryTraceability: {
          candidateHistory: true,
          criteriaDecisions: true,
          focusListEvolution: true,
          governanceChanges: true
        }
      }
    }
  },

  // EIT: Time/When Taxonomy
  {
    encodedId: 'gf.eit.time.when',
    ontology: {
      category: ['taxonomy', 'time-model', 'interrogative-dimension'],
      structures: ['hierarchical', 'categorical', 'temporal'],
      relationships: [
        {
          relationshipType: 'temporal-sequence',
          description: 'Time objects follow chronological and logical ordering'
        },
        {
          relationshipType: 'commitment-binding',
          description: 'Commitment types bind to specific time objects'
        }
      ],
      systems: ['time-management', 'project-planning', 'priority-scheduling'],
      entities: [
        'Instant',
        'Interval', 
        'Reservation',
        'Rhythm',
        'Constraint',
        'Trigger',
        'Dependency',
        'Queue',
        'Buffer',
        'Milestone'
      ],
      definitions: {
        instant: 'Single point in time (decision_time, milestone)',
        interval: 'Start + end window (availability_window, project_duration)',
        reservation: 'Scheduled effort block (work_block, meeting_time)',
        rhythm: 'Recurring pattern (weekly_cadence, review_cycle)',
        constraint: 'Rules that shape time (deadline, blackout_window, SLA)',
        trigger: 'Condition for starting (contract_signed, data_ready)',
        dependency: 'Precedence relationship (finish-to-start, lead/lag)',
        queue: 'Waiting time before work (lead_time, approval_delay)',
        buffer: 'Intentional protection time (review_buffer, contingency)',
        milestone: 'Named state change point (approval, release, acceptance)'
      }
    },
    modality: {
      attributes: [
        {
          attributeType: 'commitment-type',
          measurementScale: 'decision_time, earliest_start, latest_responsible_start, handoff_time, delivery_time, review_by, accept_by, deadline, release_time, expiry_time'
        },
        {
          attributeType: 'time-quantity',
          measurementScale: 'elapsed_duration, effort_time, lead_time, lag_time, cycle_time, throughput'
        }
      ],
      states: [
        {
          stateType: 'temporal-state',
          stateValues: 'Past, Present, Future'
        },
        {
          stateType: 'commitment-state',
          stateValues: 'Planned, Committed, In-Progress, Completed, Overdue'
        }
      ],
      contexts: ['individual-planning', 'team-coordination', 'organizational-scheduling'],
      constraints: [
        {
          constraintType: 'temporal-logic',
          description: 'Time objects must follow logical temporal ordering'
        },
        {
          constraintType: 'commitment-consistency',
          description: 'Commitment types must align with appropriate time objects'
        }
      ],
      identity: {
        stableId: 'eit-time-taxonomy-v1',
        versioning: 'v1.0',
        lineage: ['derives-from: Enhanced Interrogative Taxonomy', 'integrates-with: DEGAP Arrange phase'],
        invariants: 'Universal application for "when" aspects of priorities'
      }
    },
    dynamics: {
      rules: [
        {
          ruleType: 'temporal-ordering',
          ruleDescription: 'Time objects must follow chronological sequence',
          sourceNotes: 'Essential for project planning and priority scheduling'
        },
        {
          ruleType: 'commitment-binding',
          ruleDescription: 'Commitment types bind to specific time objects based on context',
          sourceNotes: 'Enables clear accountability and timeline management'
        }
      ],
      processes: [
        {
          processType: 'time-object-creation',
          processDescription: 'Time objects are created based on project requirements and constraints'
        },
        {
          processType: 'commitment-assignment',
          processDescription: 'Commitment types are assigned to appropriate time objects'
        },
        {
          processType: 'temporal-validation',
          processDescription: 'Time objects are validated for logical consistency'
        }
      ],
      timing: {
        'instant-creation': 'Immediate',
        'interval-definition': 'During planning phase',
        'commitment-binding': 'During commitment phase',
        note: 'Timing varies by project complexity and stakeholder requirements'
      },
      causality: [
        {
          causalType: 'temporal-dependency',
          causalDescription: 'Time objects create dependencies for subsequent activities'
        },
        {
          causalType: 'commitment-trigger',
          causalDescription: 'Commitment types trigger specific actions at defined times'
        }
      ],
      uncertainty: {
        confidenceLevels: 'High confidence in core time objects; moderate confidence in commitment patterns',
        sensitivity: 'Sensitive to project complexity and stakeholder availability',
        volatilityIndicators: 'Stable core taxonomy; variable application patterns'
      }
    },
    evaluation: {
      intent: {
        actorScope: ['individual', 'team', 'organization'],
        stakeholders: ['project-managers', 'team-members', 'stakeholders'],
        timeHorizon: ['short-term', 'medium-term', 'long-term'],
        motivationalFrame: ['clarity', 'accountability', 'predictability'],
        ethicalAnchors: ['transparency', 'fairness', 'commitment'],
        priorityAims: ['clear-timelines', 'reliable-scheduling', 'effective-planning']
      },
      utility: {
        utilityDimensions: ['clarity', 'predictability', 'accountability', 'efficiency'],
        tradeoffPolicies: ['balance flexibility vs. structure based on project complexity']
      },
      observation: {
        metrics: [
          {
            metricType: 'timeline-adherence',
            metricDescription: 'Percentage of commitments met on time'
          },
          {
            metricType: 'planning-accuracy',
            metricDescription: 'Accuracy of time estimates vs. actual duration'
          },
          {
            metricType: 'commitment-reliability',
            metricDescription: 'Reliability of commitment fulfillment'
          }
        ],
        signals: ['timeline-updates', 'commitment-changes', 'milestone-completions'],
        indicatorClasses: ['leading', 'lagging'],
        resolutionCadence: 'Weekly reviews; daily for critical timelines'
      },
      adaptation: {
        feedbackLoops: ['timeline-reviews', 'commitment-assessments'],
        scopeOfChange: ['time-object-definitions', 'commitment-patterns'],
        learningDepth: ['single-loop', 'double-loop'],
        memoryTraceability: {
          timelineHistory: true,
          commitmentDecisions: true,
          milestoneTracking: true
        }
      }
    }
  },

  // EIT: Actor/Who Taxonomy
  {
    encodedId: 'gf.eit.actor.who',
    ontology: {
      category: ['taxonomy', 'actor-model', 'interrogative-dimension'],
      structures: ['hierarchical', 'role-based', 'accountability'],
      relationships: [
        {
          relationshipType: 'role-hierarchy',
          description: 'Actors operate within defined role hierarchies'
        },
        {
          relationshipType: 'accountability-chain',
          description: 'Accountability flows through defined chains'
        }
      ],
      systems: ['organizational-structure', 'stakeholder-management', 'decision-making'],
      entities: [
        'Role',
        'Actor',
        'Authority',
        'Accountability',
        'Stakeholder'
      ],
      definitions: {
        role: 'Functional position (product_manager, tech_lead, sponsor)',
        actor: 'Specific entity (named_person, team, vendor, system_agent)',
        authority: 'Decision rights and scope (budget_authority, approval_scope)',
        accountability: 'Ownership structure (RACI assignments, escalation_path)',
        stakeholder: 'Affected parties (customer_segment, regulator, partner)'
      }
    },
    modality: {
      attributes: [
        {
          attributeType: 'commitment-type',
          measurementScale: 'accountable_owner, approver, decision_maker, consulted_party, informed_party, delegate_of, proxy_for'
        },
        {
          attributeType: 'capacity-quantity',
          measurementScale: 'capacity_hours, load_ratio, focus_allocation_pct, skill_proficiency_level'
        }
      ],
      states: [
        {
          stateType: 'actor-state',
          stateValues: 'Active, Inactive, Available, Unavailable'
        },
        {
          stateType: 'role-state',
          stateValues: 'Assigned, Unassigned, Delegated, Escalated'
        }
      ],
      contexts: ['individual-work', 'team-collaboration', 'organizational-governance'],
      constraints: [
        {
          constraintType: 'authority-boundary',
          description: 'Actors can only exercise authority within defined scope'
        },
        {
          constraintType: 'accountability-chain',
          description: 'Accountability must flow through defined organizational chains'
        }
      ],
      identity: {
        stableId: 'eit-actor-taxonomy-v1',
        versioning: 'v1.0',
        lineage: ['derives-from: Enhanced Interrogative Taxonomy', 'integrates-with: DEGAP Engage phase'],
        invariants: 'Essential for Stakeholder Intelligence capability'
      }
    },
    dynamics: {
      rules: [
        {
          ruleType: 'authority-scope',
          ruleDescription: 'Actors can only make decisions within their authority scope',
          sourceNotes: 'Prevents overreach and maintains organizational structure'
        },
        {
          ruleType: 'accountability-flow',
          ruleDescription: 'Accountability flows upward through organizational hierarchy',
          sourceNotes: 'Ensures clear ownership and escalation paths'
        }
      ],
      processes: [
        {
          processType: 'role-assignment',
          processDescription: 'Actors are assigned to roles based on capabilities and organizational needs'
        },
        {
          processType: 'authority-delegation',
          processDescription: 'Authority is delegated from higher to lower levels as needed'
        },
        {
          processType: 'stakeholder-engagement',
          processDescription: 'Stakeholders are identified and engaged based on impact and influence'
        }
      ],
      timing: {
        'role-assignment': 'During onboarding or role changes',
        'authority-delegation': 'As needed for decision-making',
        'stakeholder-engagement': 'Throughout project lifecycle',
        note: 'Timing varies by organizational structure and project requirements'
      },
      causality: [
        {
          causalType: 'authority-decision',
          causalDescription: 'Authority enables decision-making within scope'
        },
        {
          causalType: 'accountability-outcome',
          causalDescription: 'Accountability drives outcome ownership and quality'
        }
      ],
      uncertainty: {
        confidenceLevels: 'High confidence in core actor concepts; moderate confidence in organizational patterns',
        sensitivity: 'Sensitive to organizational culture and structure',
        volatilityIndicators: 'Stable core taxonomy; variable organizational implementation'
      }
    },
    evaluation: {
      intent: {
        actorScope: ['individual', 'team', 'organization'],
        stakeholders: ['employees', 'managers', 'executives', 'external-stakeholders'],
        timeHorizon: ['short-term', 'medium-term', 'long-term'],
        motivationalFrame: ['clarity', 'accountability', 'empowerment'],
        ethicalAnchors: ['transparency', 'fairness', 'responsibility'],
        priorityAims: ['clear-roles', 'effective-governance', 'stakeholder-alignment']
      },
      utility: {
        utilityDimensions: ['clarity', 'accountability', 'efficiency', 'governance'],
        tradeoffPolicies: ['balance autonomy vs. control based on organizational maturity']
      },
      observation: {
        metrics: [
          {
            metricType: 'role-clarity',
            metricDescription: 'Clarity of role definitions and responsibilities'
          },
          {
            metricType: 'authority-effectiveness',
            metricDescription: 'Effectiveness of authority delegation and usage'
          },
          {
            metricType: 'stakeholder-satisfaction',
            metricDescription: 'Stakeholder satisfaction with engagement and communication'
          }
        ],
        signals: ['role-conflicts', 'authority-issues', 'stakeholder-feedback'],
        indicatorClasses: ['leading', 'lagging'],
        resolutionCadence: 'Monthly reviews; immediate for conflicts'
      },
      adaptation: {
        feedbackLoops: ['role-effectiveness-reviews', 'authority-assessments'],
        scopeOfChange: ['role-definitions', 'authority-scopes', 'stakeholder-strategies'],
        learningDepth: ['single-loop', 'double-loop'],
        memoryTraceability: {
          roleHistory: true,
          authorityDecisions: true,
          stakeholderInteractions: true
        }
      }
    }
  },
  
  // EIT: Object/What Taxonomy
  {
    encodedId: 'gf.eit.object.what',
    ontology: {
      category: ['taxonomy', 'work-model', 'interrogative-dimension'],
      structures: ['hierarchical', 'categorical'],
      relationships: [
        { relationshipType: 'composition', description: 'Work Items produce Deliverables and satisfy Requirements' },
        { relationshipType: 'records', description: 'Decision Records capture choices and rationale' },
        { relationshipType: 'dependency', description: 'Dependencies constrain sequencing and feasibility' }
      ],
      systems: ['work-management', 'requirements-management', 'delivery'],
      entities: ['Work Item', 'Deliverable', 'Requirement', 'Decision Record', 'Dependency'],
      definitions: {
        'work_item': 'Actionable unit of work (feature, bug, task, spike)',
        'deliverable': 'Output artifact produced by work (spec, API, model, training)',
        'requirement': 'Specification that must be met (functional or nonfunctional)',
        'decision_record': 'Record of a decision and its rationale',
        'dependency': 'External requirement or constraint that affects work'
      }
    },
    modality: {
      attributes: [
        { attributeType: 'commitment-type', measurementScale: 'scope_statement, acceptance_criteria, definition_of_done, change_request, exception_waiver, version_tag' },
        { attributeType: 'quantity', measurementScale: 'size_points, complexity_score, value_score, risk_exposure' }
      ],
      states: [ { stateType: 'work-state', stateValues: 'Proposed, Approved, In-Progress, Done, Archived' } ],
      contexts: ['product-development', 'operations', 'research'],
      constraints: [
        { constraintType: 'definition-of-done', description: 'Completion requires DoD satisfied' },
        { constraintType: 'traceability', description: 'Requirements traceable to deliverables' }
      ],
      identity: {
        stableId: 'eit-object-taxonomy-v1', versioning: 'v1.0',
        lineage: ['derives-from: Enhanced Interrogative Taxonomy'],
        invariants: 'Work Items, Deliverables, Requirements, Decision Records, Dependencies'
      }
    },
    dynamics: {
      rules: [
        { ruleType: 'traceability', ruleDescription: 'Every Deliverable traces to one or more Requirements' },
        { ruleType: 'change-control', ruleDescription: 'Changes require recorded Decision Records' }
      ],
      processes: [
        { processType: 'requirements-definition', processDescription: 'Specify and validate requirements' },
        { processType: 'delivery', processDescription: 'Produce deliverables via Work Items' }
      ],
      timing: { note: 'Operates across Gather and Arrange phases' },
      causality: [ { causalType: 'requirement→work', causalDescription: 'Requirements drive Work Items' } ],
      uncertainty: { confidenceLevels: 'High on core objects', sensitivity: 'Varies by domain', volatilityIndicators: 'Moderate' }
    },
    evaluation: {
      intent: { actorScope: ['team','organization'], stakeholders: ['customers','engineers','product'] },
      utility: { utilityDimensions: ['quality','value','risk'], tradeoffPolicies: ['balance scope vs. speed'] },
      observation: { metrics: [ { metricType: 'requirements-coverage', metricDescription: 'Coverage of reqs by deliverables' } ] },
      adaptation: { feedbackLoops: ['change-review'], scopeOfChange: ['scope','requirements'] }
    }
  },

  // EIT: Why/Rationale Taxonomy
  {
    encodedId: 'gf.eit.rationale.why',
    ontology: {
      category: ['taxonomy', 'rationale-model', 'interrogative-dimension'],
      structures: ['hierarchical', 'multi-criteria'],
      relationships: [
        { relationshipType: 'governs', description: 'Rationale governs selection and prioritization' }
      ],
      systems: ['decision-making', 'prioritization'],
      entities: ['Business Value', 'Strategic Alignment', 'Risk Mitigation', 'Compliance Requirement', 'Stakeholder Need'],
      definitions: {
        'business_value': 'Expected benefit or ROI derived from the work',
        'strategic_alignment': 'Degree of alignment with strategic aims',
        'risk_mitigation': 'Reduction of exposure to threat or uncertainty',
        'compliance_requirement': 'Obligation to meet legal or policy standards',
        'stakeholder_need': 'Requirement expressed by impacted stakeholders'
      }
    },
    modality: {
      attributes: [
        { attributeType: 'commitment-type', measurementScale: 'value_proposition, success_criteria, risk_assessment, compliance_check, stakeholder_agreement' },
        { attributeType: 'quantity', measurementScale: 'value_score, alignment_score, risk_level, compliance_status' }
      ],
      states: [ { stateType: 'rationale-state', stateValues: 'Hypothesized, Validated, Rejected' } ],
      contexts: ['portfolio','product','operations'],
      constraints: [ { constraintType: 'evidence', description: 'Rationale should be evidenced' } ],
      identity: { stableId: 'eit-why-taxonomy-v1', versioning: 'v1.0', lineage: ['derives-from: Enhanced Interrogative Taxonomy'] }
    },
    dynamics: {
      rules: [ { ruleType: 'consistency', ruleDescription: 'Criteria must be consistently applied' } ],
      processes: [ { processType: 'valuation', processDescription: 'Estimate value and risk' } ],
      timing: { note: 'Central in Decide phase' },
      causality: [ { causalType: 'rationale→priority', causalDescription: 'Rationale shapes priority order' } ],
      uncertainty: { confidenceLevels: 'Moderate', sensitivity: 'Assumption quality', volatilityIndicators: 'High if context shifts' }
    },
    evaluation: {
      intent: { actorScope: ['executive','product'], stakeholders: ['customers','regulators'] },
      utility: { utilityDimensions: ['value','risk','alignment'] },
      observation: { metrics: [ { metricType: 'decision-quality', metricDescription: 'Outcomes vs. rationale' } ] },
      adaptation: { feedbackLoops: ['post-decision-review'] }
    }
  },

  // EIT: How/Method Taxonomy
  {
    encodedId: 'gf.eit.method.how',
    ontology: {
      category: ['taxonomy','method-model','interrogative-dimension'],
      structures: ['hierarchical'],
      relationships: [ { relationshipType: 'implements', description: 'Methods implement rationale to produce outcomes' } ],
      systems: ['process-improvement','tooling','practice-management'],
      entities: ['Process','Tool','Technique','Framework','Practice'],
      definitions: {
        'process': 'Ordered steps to achieve an outcome',
        'tool': 'Instrument or system used to execute a process',
        'technique': 'Specific way of applying a tool or process',
        'framework': 'Structured model guiding method selection and flow',
        'practice': 'Institutionalized way of working'
      }
    },
    modality: {
      attributes: [
        { attributeType: 'commitment-type', measurementScale: 'process_step, tool_usage, technique_application, framework_adoption, practice_standard' },
        { attributeType: 'quantity', measurementScale: 'process_efficiency, tool_proficiency, technique_mastery, framework_adoption_rate' }
      ],
      states: [ { stateType: 'maturity', stateValues: 'Ad-hoc, Managed, Defined, Measured, Optimizing' } ],
      contexts: ['engineering','design','operations'],
      constraints: [ { constraintType: 'fitness', description: 'Methods must fit context and capability' } ],
      identity: { stableId: 'eit-how-taxonomy-v1', versioning: 'v1.0' }
    },
    dynamics: {
      rules: [ { ruleType: 'appropriateness', ruleDescription: 'Choose the smallest sufficient method' } ],
      processes: [ { processType: 'method-selection', processDescription: 'Select tools and techniques based on aim' } ],
      timing: { note: 'Prominent in Engage/Gather phases' },
      causality: [ { causalType: 'method→outcome', causalDescription: 'Right method increases success odds' } ],
      uncertainty: { confidenceLevels: 'Moderate', sensitivity: 'Skill and adoption', volatilityIndicators: 'Medium' }
    },
    evaluation: {
      intent: { actorScope: ['team'], stakeholders: ['operators','customers'] },
      utility: { utilityDimensions: ['efficiency','quality'] },
      observation: { metrics: [ { metricType: 'cycle-time', metricDescription: 'Process cycle time' } ] },
      adaptation: { feedbackLoops: ['retro','kaizen'] }
    }
  },

  // EIT: Where/Location Taxonomy
  {
    encodedId: 'gf.eit.location.where',
    ontology: {
      category: ['taxonomy','location-model','interrogative-dimension'],
      structures: ['hierarchical','categorical'],
      relationships: [ { relationshipType: 'allocation', description: 'Work allocated across spaces/units/regions' } ],
      systems: ['org-design','field-ops','market-ops'],
      entities: ['Physical Space','Digital Space','Organizational Unit','Geographic Region','Market Segment'],
      definitions: {
        'physical_space': 'Real-world location where work occurs',
        'digital_space': 'Virtual environment or platform',
        'organizational_unit': 'Department or team context',
        'geographic_region': 'Territorial grouping for operations',
        'market_segment': 'Customer group targeted by offerings'
      }
    },
    modality: {
      attributes: [
        { attributeType: 'commitment-type', measurementScale: 'location_assignment, space_reservation, unit_assignment, region_coverage, market_targeting' },
        { attributeType: 'quantity', measurementScale: 'space_capacity, geographic_coverage, market_penetration, organizational_reach' }
      ],
      states: [ { stateType: 'availability', stateValues: 'Available, Reserved, Occupied, Offline' } ],
      contexts: ['facilities','cloud','sales'],
      constraints: [ { constraintType: 'access', description: 'Access limitations by policy or capability' } ],
      identity: { stableId: 'eit-where-taxonomy-v1', versioning: 'v1.0' }
    },
    dynamics: {
      rules: [ { ruleType: 'fit', ruleDescription: 'Locations must support required modality and throughput' } ],
      processes: [ { processType: 'allocation', processDescription: 'Allocate work to spaces/regions/units' } ],
      timing: { note: 'Used in Gather phase for scoping' },
      causality: [ { causalType: 'location→feasibility', causalDescription: 'Location affects feasibility and cost' } ],
      uncertainty: { confidenceLevels: 'Moderate', sensitivity: 'External constraints', volatilityIndicators: 'Medium' }
    },
    evaluation: {
      intent: { actorScope: ['operations','sales'] },
      utility: { utilityDimensions: ['coverage','cost'] },
      observation: { metrics: [ { metricType: 'coverage-index', metricDescription: 'Coverage vs. target' } ] },
      adaptation: { feedbackLoops: ['reallocation-reviews'] }
    }
  },

  // EIT: Extent/Degree Taxonomy
  {
    encodedId: 'gf.eit.extent.degree',
    ontology: {
      category: ['taxonomy','extent-model','interrogative-dimension'],
      structures: ['scales','thresholds'],
      relationships: [ { relationshipType: 'bounds', description: 'Extent sets bounds and thresholds on work' } ],
      systems: ['governance','quality','capacity-planning'],
      entities: ['Scope Boundary','Quality Threshold','Performance Target','Capacity Limit','Priority Level'],
      definitions: {
        'scope_boundary': 'Explicit boundary of inclusion/exclusion',
        'quality_threshold': 'Minimum acceptable quality level',
        'performance_target': 'Desired performance objective',
        'capacity_limit': 'Upper bound on resource usage',
        'priority_level': 'Relative importance weight or class'
      }
    },
    modality: {
      attributes: [
        { attributeType: 'commitment-type', measurementScale: 'scope_boundary, quality_standard, performance_target, capacity_constraint, priority_assignment' },
        { attributeType: 'quantity', measurementScale: 'scope_size, quality_score, performance_metric, capacity_utilization, priority_weight' }
      ],
      states: [ { stateType: 'satisfaction', stateValues: 'Below, Meets, Exceeds' } ],
      contexts: ['portfolio','project'],
      constraints: [ { constraintType: 'guardrail', description: 'Non-negotiable bounds or limits' } ],
      identity: { stableId: 'eit-extent-taxonomy-v1', versioning: 'v1.0' }
    },
    dynamics: {
      rules: [ { ruleType: 'guardrails', ruleDescription: 'Operate within defined bounds' } ],
      processes: [ { processType: 'target-setting', processDescription: 'Set thresholds and targets' } ],
      timing: { note: 'Arrange phase emphasis' },
      causality: [ { causalType: 'threshold→decision', causalDescription: 'Thresholds trigger decisions' } ],
      uncertainty: { confidenceLevels: 'Moderate', sensitivity: 'Measurement quality', volatilityIndicators: 'Low–Medium' }
    },
    evaluation: {
      intent: { actorScope: ['leadership'] },
      utility: { utilityDimensions: ['predictability','fairness'] },
      observation: { metrics: [ { metricType: 'SLA-adherence', metricDescription: 'Adherence to thresholds' } ] },
      adaptation: { feedbackLoops: ['guardrail-adjustment'] }
    }
  },

  // EIT: Cross-Dimension Integration
  {
    encodedId: 'gf.eit.integration.cross_dimension',
    ontology: {
      category: ['integration','interrogative-dimensions'],
      structures: ['relational'],
      relationships: [
        { relationshipType: 'who×what', description: 'responsibility(role, work_item, RACI_type)' },
        { relationshipType: 'who×when', description: 'availability(actor, time_window)' },
        { relationshipType: 'what×when', description: 'schedule(work_item, timeline)' }
      ],
      systems: ['planning','scheduling','governance'],
      entities: ['responsibility','availability','schedule'],
      definitions: {
        'responsibility': 'Assignment of role accountability to work items',
        'availability': 'Actor availability across time windows',
        'schedule': 'Mapping of work items to timelines'
      }
    },
    modality: {
      attributes: [ { attributeType: 'integration-pattern', measurementScale: 'RACI, availability_window, project_timeline' } ],
      states: [ { stateType: 'consistency', stateValues: 'Consistent, Inconsistent' } ],
      contexts: ['cross-functional','program'],
      constraints: [ { constraintType: 'consistency', description: 'Integrated views must remain consistent across dimensions' } ],
      identity: { stableId: 'eit-cross-dimension-v1', versioning: 'v1.0' }
    },
    dynamics: {
      rules: [ { ruleType: 'shared-keys', ruleDescription: 'Integration requires shared identifiers across dimensions' } ],
      processes: [ { processType: 'crosswalk', processDescription: 'Establish cross-dimension mappings' } ],
      timing: { note: 'Used during Arrange and Prioritize phases' },
      causality: [ { causalType: 'integration→coherence', causalDescription: 'Integration improves coherence and execution' } ],
      uncertainty: { confidenceLevels: 'Moderate', sensitivity: 'Data quality', volatilityIndicators: 'Medium' }
    },
    evaluation: {
      intent: { actorScope: ['program','portfolio'] },
      utility: { utilityDimensions: ['coherence','predictability'] },
      observation: { metrics: [ { metricType: 'integration-consistency', metricDescription: 'Conflicts across dimension joins' } ] },
      adaptation: { feedbackLoops: ['mapping-updates'] }
    }
  },

  {
    encodedId: 'gf.relevance.framework',
    ontology: {
      category: ['framework', 'communication-model', 'decision-support'],
      structures: ['triplet', 'sequential', 'hierarchical'],
      relationships: [
        {
          relationshipType: 'applies-to',
          description: 'Universal application across personal, team, and organizational contexts'
        },
        {
          relationshipType: 'prevents',
          description: 'Guards against shallow communication, ungrounded inspiration, and mechanical instruction'
        }
      ],
      systems: ['communication', 'leadership', 'prioritization', 'decision-making', 'coaching'],
      entities: [
        'What is it? (Essence)',
        'Why is it important? (Significance)',
        'How does it work? (Mechanics)'
      ],
      definitions: {
        'relevance': 'The quality of being connected to significance, clarity, and practical application',
        'coherence': 'Alignment between concept (what), purpose (why), and practice (how)',
        'shallow communication': 'Communication that provides definition without significance or application',
        'ungrounded inspiration': 'Inspiration that lacks clear definition or practical mechanics',
        'mechanical instruction': 'Instructions on "how" without understanding of "what" or "why"'
      }
    },
    modality: {
      attributes: [
        {
          attributeType: 'clarity',
          measurementScale: 'Low / Medium / High - based on precision of essence definition'
        },
        {
          attributeType: 'significance',
          measurementScale: 'Low / Medium / High - based on connection to value and stakes'
        },
        {
          attributeType: 'applicability',
          measurementScale: 'Low / Medium / High - based on demonstrable mechanics and practice'
        },
        {
          attributeType: 'coherence',
          measurementScale: 'Aligned / Partially Aligned / Misaligned - across all three dimensions'
        }
      ],
      states: [
        {
          stateType: 'completeness',
          stateValues: 'What-only, Why-only, How-only, What+Why, What+How, Why+How, Complete (What+Why+How)'
        },
        {
          stateType: 'application-readiness',
          stateValues: 'Conceptual, Significant, Actionable, Fully Relevant'
        }
      ],
      contexts: ['leadership-communication', 'team-alignment', 'organizational-strategy', 'coaching', 'prioritization'],
      constraints: [
        {
          constraintType: 'sequence-dependency',
          description: 'The three questions must be addressed sequentially for maximum effectiveness'
        },
        {
          constraintType: 'context-sensitivity',
          description: 'Depth and specificity of each answer varies by audience and purpose'
        }
      ],
      identity: {
        stableId: 'relevance-framework-v1',
        versioning: 'v1.0',
        lineage: ['foundational: Peak Priorities communication principles'],
        invariants: 'Three-question structure (What-Why-How) remains constant across all applications'
      }
    },
    dynamics: {
      rules: [
        {
          ruleType: 'sequential-progression',
          ruleDescription: 'Address What → Why → How in order; each builds on the previous',
          sourceNotes: 'Definition enables significance; significance enables meaningful application'
        },
        {
          ruleType: 'anti-pattern-prevention',
          ruleDescription: 'Avoid what-only (no meaning), why-only (no grounding), or how-only (no context)',
          sourceNotes: 'Partial application reduces relevance and effectiveness'
        },
        {
          ruleType: 'scope-adaptation',
          ruleDescription: 'Framework applies at individual, team, and organizational levels with adjusted specificity',
          sourceNotes: 'Same structure, different depth based on scope'
        }
      ],
      processes: [
        {
          processType: 'communication-refinement',
          processDescription: 'Iteratively answer each question until clarity, significance, and application are achieved'
        },
        {
          processType: 'coherence-validation',
          processDescription: 'Check alignment across all three dimensions before finalizing communication or decision'
        }
      ],
      timing: {
        'individual-application': '5-15 minutes for personal clarity',
        'team-application': '30-60 minutes for team alignment',
        'organizational-application': 'Multiple sessions for strategic initiatives',
        'note': 'Time investment scales with complexity and stakeholder diversity'
      },
      causality: [
        {
          causalType: 'enabling',
          causalDescription: 'Clear "what" enables meaningful "why"; meaningful "why" enables actionable "how"'
        },
        {
          causalType: 'preventive',
          causalDescription: 'Complete framework prevents miscommunication, misalignment, and ineffective action'
        }
      ],
      uncertainty: {
        confidenceLevels: 'High confidence in framework structure; moderate confidence in consistent application',
        sensitivity: 'Effectiveness sensitive to user discipline in completing all three dimensions',
        volatilityIndicators: 'Stable framework; application quality varies by context and user maturity'
      }
    },
    evaluation: {
      intent: {
        actorScope: ['individual', 'team', 'organization'],
        stakeholders: ['leaders', 'team members', 'coaches', 'facilitators', 'executives'],
        timeHorizon: ['near', 'mid', 'long'],
        motivationalFrame: ['clarity', 'alignment', 'effectiveness', 'congruence'],
        ethicalAnchors: ['transparency', 'shared understanding', 'informed action'],
        priorityAims: ['clear communication', 'meaningful connection', 'practical application']
      },
      utility: {
        utilityDimensions: ['clarity', 'significance', 'applicability', 'coherence'],
        tradeoffPolicies: ['Balance depth vs. brevity based on context; prioritize completeness over speed']
      },
      observation: {
        metrics: [
          {
            metricType: 'framework-completeness',
            metricDescription: 'Percentage of communications/decisions addressing all three questions'
          },
          {
            metricType: 'coherence-score',
            metricDescription: 'Assessment of alignment across what-why-how dimensions'
          },
          {
            metricType: 'communication-effectiveness',
            metricDescription: 'Reduction in clarification requests and misunderstandings'
          },
          {
            metricType: 'action-quality',
            metricDescription: 'Rate of successful implementation following framework application'
          }
        ],
        signals: ['stakeholder comprehension', 'follow-up questions', 'implementation success', 'team alignment'],
        indicatorClasses: ['leading', 'direct'],
        resolutionCadence: 'Real-time during communication; retrospective in team reviews'
      },
      adaptation: {
        feedbackLoops: ['immediate', 'scheduled'],
        scopeOfChange: ['depth-of-answers', 'context-specificity', 'stakeholder-targeting'],
        learningDepth: ['single-loop', 'double-loop'],
        memoryTraceability: {
          communicationRecords: true,
          coherenceAssessments: true,
          applicationOutcomes: true
        }
      }
    }
  },

  // Role Types
  {
    encodedId: 'pf.framework.diagnostic.role.types',
    ontology: {
      category: ['taxonomy', 'classification', 'framework'],
      structures: ['list', 'hierarchy'],
      relationships: [
        {
          relationshipType: 'part-of',
          description: 'Component of the Prioritization Diagnostic Framework'
        },
        {
          relationshipType: 'used-by',
          description: 'Used in Role × Scope Matrix and Role-Based Participation models'
        }
      ],
      systems: ['prioritization-diagnostic-framework', 'organizational-structure', 'leadership-development'],
      entities: [
        'Individual Contributor',
        'Team Lead / Manager',
        'Executive / Org Leader'
      ],
      definitions: {
        'role type': 'A classification of organizational position based on scope of influence and responsibility',
        'scope of influence': 'The breadth and depth of impact a role has across individual, team, and organizational levels'
      }
    },
    modality: {
      attributes: [
        {
          attributeType: 'scope-of-influence',
          measurementScale: 'Individual / Team / Organization'
        },
        {
          attributeType: 'decision-authority',
          measurementScale: 'Low / Medium / High'
        },
        {
          attributeType: 'leadership-responsibility',
          measurementScale: 'None / Team-Level / Organization-Level'
        }
      ],
      states: [
        {
          stateType: 'multi-state',
          stateValues: 'IC Only, IC with Leadership, Full Leadership'
        }
      ],
      contexts: ['organizational-hierarchy', 'team-structure', 'prioritization-scope'],
      constraints: [
        {
          constraintType: 'organizational-size',
          description: 'Role distinctions may blur in very small organizations'
        },
        {
          constraintType: 'matrix-organizations',
          description: 'Individuals may hold multiple role types simultaneously in matrix structures'
        }
      ],
      identity: {
        stableId: 'role-types-v1',
        versioning: 'v1.0',
        lineage: ['part-of: Prioritization Diagnostic Framework'],
        invariants: 'Three-tier classification based on scope and leadership responsibility'
      }
    },
    dynamics: {
      rules: [
        {
          ruleType: 'progression-pattern',
          ruleDescription: 'Career progression typically moves IC → Team Lead → Executive, though individual contributors can advance without taking on leadership',
          sourceNotes: 'Dual-track career paths recognize IC and leadership trajectories'
        },
        {
          ruleType: 'participation-variation',
          ruleDescription: 'Each role type engages with prioritization at all three scope levels (individual, team, org) but nature of participation differs by role',
          sourceNotes: 'See Role-Based Participation Across Scopes matrix'
        }
      ],
      processes: [
        {
          processType: 'role-transition',
          processDescription: 'Moving between role types requires skill development, mindset shifts, and organizational recognition'
        },
        {
          processType: 'dual-capacity',
          processDescription: 'Team Leads balance IC work with leadership; ratio shifts based on team size and organizational needs'
        }
      ],
      timing: {
        'IC-to-TeamLead': 'Typically 2-5 years in IC role before transition',
        'TeamLead-to-Executive': 'Varies widely; 3-10+ years managing teams',
        note: 'Timing highly contextual based on organization, industry, and individual readiness'
      },
      causality: [
        {
          causalType: 'direct',
          causalDescription: 'Role type determines primary focus area for prioritization (personal vs team vs org)'
        },
        {
          causalType: 'enabling',
          causalDescription: 'Clear role definitions enable appropriate delegation and escalation of prioritization decisions'
        }
      ],
      uncertainty: {
        confidenceLevels: 'High confidence in three-tier model across most organizations',
        sensitivity: 'Model may need adaptation for very flat or highly matrixed organizations',
        volatilityIndicators: 'Stable classification; edge cases exist in hybrid roles'
      }
    },
    evaluation: {
      intent: {
        actorScope: ['individual', 'team', 'organization'],
        stakeholders: ['employees at all levels', 'HR/talent development', 'organizational design teams'],
        timeHorizon: ['near', 'mid', 'long'],
        motivationalFrame: ['clarity', 'development', 'organizational effectiveness'],
        ethicalAnchors: ['fairness', 'growth opportunity', 'appropriate responsibility'],
        priorityAims: ['role clarity', 'effective delegation', 'leadership development']
      },
      utility: {
        utilityDimensions: ['clarity', 'alignment', 'development', 'organizational design'],
        tradeoffPolicies: ['Balance simplicity of three categories vs. nuance of hybrid roles']
      },
      observation: {
        metrics: [
          {
            metricType: 'role-clarity-score',
            metricDescription: 'Employee understanding of their role type and associated expectations'
          },
          {
            metricType: 'decision-escalation-appropriateness',
            metricDescription: 'Rate of prioritization decisions made at appropriate role level'
          },
          {
            metricType: 'leadership-readiness',
            metricDescription: 'Assessment of IC readiness for Team Lead transition'
          }
        ],
        signals: ['role confusion incidents', 'delegation patterns', 'career development conversations'],
        indicatorClasses: ['leading', 'direct'],
        resolutionCadence: 'Quarterly talent reviews; annual role assessments'
      },
      adaptation: {
        feedbackLoops: ['scheduled', 'event-driven'],
        scopeOfChange: ['role definitions', 'transition criteria', 'hybrid role recognition'],
        learningDepth: ['single-loop', 'double-loop'],
        memoryTraceability: {
          roleDefinitions: true,
          transitionHistory: true,
          organizationalContext: true
        }
      }
    }
  },

  // Open Questions Framework (OQF)
  {
    encodedId: 'oqf.framework',
    ontology: {
      category: ['framework', 'process', 'methodology'],
      structures: ['sequence', 'phased', 'iterative'],
      relationships: [
        {
          relationshipType: 'contains',
          description: 'Contains five core functions: Frame Question, Surface Theory, Identify Source of Truth, Define Path of Inquiry, Review & Reflect'
        },
        {
          relationshipType: 'enables',
          description: 'Enables movement from uncertainty to clarity and accelerates learning velocity'
        },
        {
          relationshipType: 'used-by',
          description: 'Used by teams and individuals to operationalize clarity and manage uncertainty'
        }
      ],
      systems: ['open-questions-framework', 'clarity-acceleration', 'decision-support', 'learning-velocity'],
      entities: [
        'Frame the Question',
        'Surface Current Theory',
        'Identify Source of Truth',
        'Define Path of Inquiry',
        'Review & Reflect',
        'High-Leverage Unknown',
        'Current Theory',
        'Smallest Decisive Experiment (SDE)',
        'Open Questions Register',
        'Evidence Quality'
      ],
      definitions: {
        'high_leverage_unknown': 'A question whose answer unlocks significant decision-making or learning',
        'current_theory': 'Explicit statement of what is believed to be true, with evidence quality label',
        'source_of_truth': 'Where reliable evidence lives (data, experiments, experts, etc.)',
        'smallest_decisive_experiment': 'Minimum viable learning step that is Small (fast signal), Decisive (clear if-then rule), and Truth-Oriented (tests real unknown)',
        'evidence_quality': 'Label indicating strength of evidence: Anecdote, Directional, Strong, Verified',
        'clarity_velocity': 'Rate at which uncertainty transforms into clarity, measured by Decision Latency, Evidence Upgrade Rate, and Freshness Rate'
      }
    },
    modality: {
      attributes: [
        {
          attributeType: 'evidence_quality',
          measurementScale: 'Anecdote (1) / Directional (2) / Strong (3) / Verified (4)'
        },
        {
          attributeType: 'question_leverage',
          measurementScale: 'Leverage = (Impact × Dependency) ÷ Effort-to-Learn, scored 1-5 per variable'
        },
        {
          attributeType: 'clarity_state',
          measurementScale: 'Unclear / Partially Clear / Clear / Verified'
        },
        {
          attributeType: 'sde_readiness',
          measurementScale: 'Not Defined / Defined / In Progress / Completed / Invalidated'
        }
      ],
      states: [
        {
          stateType: 'question_lifecycle',
          stateValues: 'Identified → Framed → Theory Surfaces → SDE Defined → SDE Executed → Theory Validated/Invalidated → Closed'
        },
        {
          stateType: 'register_status',
          stateValues: 'Active / In Review / Resolved / Archived'
        },
        {
          stateType: 'theory_confidence',
          stateValues: 'Hypothesis / Theory / Validated / Invalidated'
        }
      ],
      contexts: ['team-decision-making', 'project-planning', 'strategic-inquiry', 'product-development', 'organizational-learning'],
      constraints: [
        {
          constraintType: 'question_quality',
          description: 'Questions must meet 3-Point Checklist: Singular Unknown, Decision-Linked, Neutral & Investigable'
        },
        {
          constraintType: 'sde_criteria',
          description: 'Smallest Decisive Experiments must be Small (fast), Decisive (clear if-then), and Truth-Oriented (tests unknown)'
        },
        {
          constraintType: 'evidence_labeling',
          description: 'All current theories must be labeled with evidence quality scale'
        }
      ],
      identity: {
        stableId: 'oqf-framework-v1',
        versioning: 'v1.0',
        lineage: ['derives-from: structured inquiry methodologies', 'integrates-with: learning velocity concepts', 'inspired-by: scientific method and lean startup'],
        invariants: 'Five-function structure remains constant; operationalizes uncertainty as trackable work items'
      }
    },
    dynamics: {
      rules: [
        {
          ruleType: 'sequential-progression',
          ruleDescription: 'Five functions typically proceed in sequence: Frame → Surface → Identify → Define → Review, though functions can be revisited iteratively',
          sourceNotes: 'Framework is designed for iterative application, not strict linear progression'
        },
        {
          ruleType: 'question_prioritization',
          ruleDescription: 'Questions ranked by leverage: (Impact × Dependency) ÷ Effort-to-Learn; focus on highest leverage questions first',
          sourceNotes: 'Prevents analysis paralysis by focusing effort on questions that unlock the most value'
        },
        {
          ruleType: 'evidence_evolution',
          ruleDescription: 'Evidence quality should upgrade over time (Anecdote → Directional → Strong → Verified) through SDEs and inquiry',
          sourceNotes: 'Tracking evidence quality prevents overconfidence in weak evidence'
        },
        {
          ruleType: 'register_maintenance',
          ruleDescription: 'Open Questions Register must be reviewed regularly (weekly recommended) to maintain freshness and update status',
          sourceNotes: 'Register is operating system, not archive; requires active maintenance'
        }
      ],
      processes: [
        {
          processType: 'question_framing',
          processDescription: 'Transform vague concerns into high-leverage unknowns using 3-Point Checklist; ensure question is singular, decision-linked, and investigable'
        },
        {
          processType: 'theory_surfacing',
          processDescription: 'Make implicit assumptions explicit by stating current theory and labeling evidence quality; use Clarity Ladder to identify what is actually known vs. assumed'
        },
        {
          processType: 'sde_design',
          processDescription: 'Design smallest decisive experiment that tests the unknown; must be small (fast signal), decisive (clear if-then rule), and truth-oriented (tests real unknown)'
        },
        {
          processType: 'register_creation',
          processDescription: 'Create Open Questions Register entry with: Question, Owner, Current Beliefs, Evidence Quality, Next Step (SDE), Review Date, Status'
        },
        {
          processType: 'clarity_measurement',
          processDescription: 'Track three core metrics: Decision Latency (speed), Evidence Upgrade Rate (depth), Freshness Rate (discipline)'
        }
      ],
      timing: {
        'oqf_lite_loop': '5-15 minutes for quick application',
        'full_question_cycle': 'Hours to days depending on SDE complexity',
        'register_review': 'Weekly recommended; daily for critical projects',
        'sde_execution': 'Varies by experiment design; typically days to weeks',
        note: 'Timing scales with question complexity and stakeholder involvement'
      },
      causality: [
        {
          causalType: 'enabling',
          causalDescription: 'Framing high-leverage question → enables focused inquiry → accelerates clarity'
        },
        {
          causalType: 'preventive',
          causalDescription: 'Surfacing current theory → prevents hard-coding assumptions → reduces rework'
        },
        {
          causalType: 'accelerating',
          causalDescription: 'SDE design → provides fast learning signal → upgrades evidence quality → reduces decision latency'
        },
        {
          causalType: 'measuring',
          causalDescription: 'Tracking clarity metrics → makes learning visible → improves discipline and velocity'
        }
      ],
      uncertainty: {
        confidenceLevels: 'High confidence in framework structure; moderate confidence in consistent application across contexts',
        sensitivity: 'Effectiveness sensitive to team discipline in maintaining register and executing SDEs',
        volatilityIndicators: 'Stable framework; application quality and learning velocity vary by team maturity and context'
      }
    },
    evaluation: {
      intent: {
        actorScope: ['individual', 'team', 'organization'],
        stakeholders: ['decision-makers', 'project-managers', 'product-teams', 'strategic-planners', 'coaches'],
        timeHorizon: ['near', 'mid', 'long'],
        motivationalFrame: ['clarity', 'learning', 'evidence-based-decision-making', 'reduced-rework'],
        ethicalAnchors: ['intellectual-honesty', 'evidence-based-action', 'transparency', 'continuous-learning'],
        priorityAims: ['operationalize-clarity', 'accelerate-learning-velocity', 'reduce-assumption-risk', 'improve-decision-quality']
      },
      utility: {
        utilityDimensions: ['clarity', 'learning-velocity', 'decision-quality', 'reduced-rework', 'team-alignment'],
        tradeoffPolicies: ['Balance thoroughness vs. speed based on decision stakes; use OQF-Lite for low-stakes, full framework for high-stakes']
      },
      observation: {
        metrics: [
          {
            metricType: 'decision-latency',
            metricDescription: 'Time from question identification to decision-ready clarity (days/weeks)'
          },
          {
            metricType: 'evidence-upgrade-rate',
            metricDescription: 'Rate at which evidence quality upgrades (Anecdote → Verified) over time'
          },
          {
            metricType: 'freshness-rate',
            metricDescription: 'Percentage of Open Questions Register entries reviewed within review window'
          },
          {
            metricType: 'sde-success-rate',
            metricDescription: 'Percentage of SDEs that successfully generate learning signal'
          },
          {
            metricType: 'question-resolution-rate',
            metricDescription: 'Rate at which open questions are resolved vs. accumulated'
          }
        ],
        signals: ['register-review-completion', 'evidence-quality-upgrades', 'decision-clarity-improvements', 'sde-execution-completion'],
        indicatorClasses: ['leading', 'direct'],
        resolutionCadence: 'Weekly register reviews; real-time SDE tracking; monthly clarity velocity dashboards'
      },
      adaptation: {
        feedbackLoops: ['weekly-register-reviews', 'sde-retrospectives', 'clarity-velocity-assessments'],
        scopeOfChange: ['question-framing-techniques', 'sde-design-patterns', 'register-maintenance-cadence', 'metric-thresholds'],
        learningDepth: ['single-loop', 'double-loop'],
        memoryTraceability: {
          questionHistory: true,
          theoryEvolution: true,
          sdeOutcomes: true,
          clarityMetrics: true,
          registerEvolution: true
        }
      }
    }
  }
];

// Helper function to get full encoding for an entry by ID
export const getFullEncoding = (encodedId: string): FullyEncodedEntry | undefined => {
  return fullyEncodedEntries.find(entry => entry.encodedId === encodedId);
};

// Helper to check if an entry has full encoding
export const hasFullEncoding = (encodedId: string): boolean => {
  return fullyEncodedEntries.some(entry => entry.encodedId === encodedId);
};
